import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getActiveChip } from "core-integration/src/store/chips/reducers";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { IElement } from "core-integration/src/store/elements/types";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { resetLastChange } from "core-integration/src/store/squad/actions";
import {
  canAutocomplete,
  canReset,
  getErrors,
  getLastChange,
  getProposedElements,
  getSavedPicks,
  getSquadError,
  getSquadMode,
} from "core-integration/src/store/squad/reducers";
import { fetchAutopick, reset } from "core-integration/src/store/squad/thunks";
import { IAutopickPreferences } from "core-integration/src/store/squad/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import Alert from "plos/src/components/alerts/Alert";
import { Button } from "plos/src/components/buttons/Button";
import DeadlineBar from "plos/src/components/DeadlineBar";
import SectionHeader from "plos/src/components/SectionHeader";
import { contentMain } from "plos/src/layouts";
import { edgeToEdge } from "plos/src/styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useOnboardingContext } from "../../../contexts/OnboardingContext";
import { OverlayProvider } from "../../../contexts/OverlayContext";
import { useTrackingContext } from "../../../contexts/TrackingContext";
import { usePitchView } from "../../../hooks/usePitchView";
import { PitchControls } from "../../PitchControls";
import { PlayerPreviewSheet } from "../../PlayerSheets/PlayerPreviewSheet";
import { ConfirmTransfersSheet } from "../ConfirmTransfersSheet";
import { getCodeFromError } from "../helpers";
import Onboarding from "../Onboarding/Onboarding";
import { PlayerActions } from "../PlayerActions";
import SquadListTable from "../SquadListTable";
import { SquadPitch } from "../SquadPitch";
import TransfersListTable from "../TransfersListTable";
import { PlayerActionType } from "../types";
import SquadActionsBar from "./SquadActionsBar/SquadActionsBar";
import { TutorialWarningDialog } from "./TutorialWarningDialog";
import { ManageSquadProps } from "./types";

const ManageSquad = ({
  title,
  scoreboard,
  handleOpenElemListSheet,
  elementPosition,
  setElementPosition,
}: ManageSquadProps) => {
  const { t } = useTranslation("squad");
  const { firePageViewEvent } = useTrackingContext();
  const { activeView, handleViewChange } = usePitchView();
  const [isAwaitingEntry, setIsAwaitingEntry] = useState(false);
  const [autoPickStatus, setAutoPickStatus] = useState<
    "success" | "failed" | "picking" | null
  >(null);
  const [autoPickErrorCode, setAutoPickErrorCode] = useState<string | null>(
    null,
  );
  const [tutorialWarningDialogOpen, setTutorialWarningDialogOpen] =
    useState(false);

  const player = useSelector((state: RootState) => getPlayerData(state));
  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );

  const pitchUnitRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Callback to set refs for each position
  const setPitchUnitRef = useCallback(
    (position: number) => (element: HTMLDivElement | null) => {
      pitchUnitRefs.current[position] = element;
    },
    [],
  );

  // Function to focus a specific position
  const focusPitchUnit = useCallback((position: number) => {
    const element = pitchUnitRefs.current[position];
    if (element) {
      element.focus();
    }
  }, []);

  const activeChip = useSelector(getActiveChip);
  const canUserAutocomplete = useSelector((state: RootState) =>
    canAutocomplete(state, undefined, activeChip?.id),
  );
  const canUserReset = useSelector(canReset);

  const elementsById = useSelector((state: RootState) =>
    getElementsById(state, undefined, activeChip?.id),
  );
  const errors = useSelector((state: RootState) =>
    getErrors(state, undefined, activeChip?.id),
  );
  const lastChange = useSelector(getLastChange);
  const mode = useSelector(getSquadMode);
  const proposedElements = useSelector((state: RootState) =>
    getProposedElements(state, undefined, activeChip?.id),
  );
  const rules = useSelector((state: RootState) =>
    getRules(state, undefined, activeChip?.id),
  );
  const savedPicks = useSelector(getSavedPicks);
  const teamsById = useSelector(getTeamsById);
  const error = useSelector(getSquadError);

  const {
    isOpen: isOnboardingOpen,
    submitEntry,
    reopenForTutorial,
    isTutorialEnabled,
    entryDetails,
    autopickChoice,
    lastAutopickPreferences,
  } = useOnboardingContext();

  const handleClosePlayerSheet = () => setElementPosition(0);

  let element: IElement | undefined;
  let actionMode: PlayerActionType | undefined;

  if (proposedElements[elementPosition]) {
    element = proposedElements[elementPosition];
    actionMode = "remove";
  } else if (savedPicks[elementPosition]) {
    element = elementsById[savedPicks[elementPosition].element];
    actionMode = "restore";
  }

  const dispatch = useDispatch<ThunkDispatch>();

  const autoPick = async () => {
    setAutoPickStatus("picking");
    const existing_elements = Object.values(proposedElements).map((e) => e.id);
    // Only reuse the last wizard preferences (club/spend/attack) during
    // selection; other modes just build around the current squad.
    const preferences: IAutopickPreferences =
      mode === "selection" ? { ...(lastAutopickPreferences ?? {}) } : {};
    if (existing_elements.length > 0) {
      preferences.existing_elements = existing_elements;
    }
    const result = await dispatch(fetchAutopick(preferences));
    if (result.success) {
      setAutoPickStatus("success");
      setAutoPickErrorCode(null);
    } else {
      setAutoPickStatus("failed");
      setAutoPickErrorCode(result.errorCode ?? null);
    }
    // Clear the per-element change so the status reads "auto pick
    // successful/failed" rather than announcing the last player added.
    dispatch(resetLastChange());
  };

  const handleShowMenuForElement = (position: number) =>
    setElementPosition(position);

  const handleReset = () => {
    setAutoPickStatus(null);
    setAutoPickErrorCode(null);
    dispatch(reset());
  };

  // "Auto Pick Again" resets first, then runs autopick with team builder preferences
  const autoPickAgain = async () => {
    handleReset();
    setAutoPickStatus("picking");

    const result = await dispatch(fetchAutopick(lastAutopickPreferences ?? {}));
    if (result.success) {
      setAutoPickStatus("success");
      setAutoPickErrorCode(null);
    } else {
      setAutoPickStatus("failed");
      setAutoPickErrorCode(result.errorCode ?? null);
    }
    // Clear the per-element change so the status reads "auto pick
    // successful/failed" rather than announcing the last player added.
    dispatch(resetLastChange());
  };

  const handleAutoPickWithTutorial = () => {
    if (Object.keys(proposedElements).length > 0) {
      setTutorialWarningDialogOpen(true);
    } else {
      handleReset();
      reopenForTutorial();
    }
  };

  useEffect(() => {
    dispatch(resetLastChange());
  }, [dispatch]);

  useEffect(() => {
    if (mode === "transfers") {
      firePageViewEvent(
        "fantasy transfers",
        activeView === "pitch" ? "pitch view" : "list view",
      );
    }
  }, []);

  const autopickErrorMessages: Record<string, string | undefined> = {
    autopick_failed: t(
      "manageSquad.autoPickFailed",
      "Unable to Auto Pick - no available players within budget",
    ),
  };
  const defaultAutopickErrorMessage = t(
    "manageSquad.autoPickError",
    "Unable to Auto Pick - please try again",
  );

  let latestAction: React.ReactNode = null;
  if (autoPickStatus === "picking") {
    latestAction = (
      <Alert isInline isCentered>
        <span role="status" aria-atomic="true">
          {t("manageSquad.autoPicking", "Auto Picking your squad…")}
        </span>
      </Alert>
    );
  } else if (lastChange.type === "addition") {
    latestAction = (
      <Alert isInline isCentered>
        <span role="status" aria-atomic="true">
          <Trans
            ns="squad"
            i18nKey="element.addition"
            values={{ element: elementsById[lastChange.element].web_name }}
            components={{ strong: <strong /> }}
          >
            <strong>{"{{ element }}"}</strong> has been added to your squad
          </Trans>
        </span>
      </Alert>
    );
  } else if (lastChange.type === "removal") {
    latestAction = (
      <Alert isInline isCentered>
        <span role="status" aria-atomic="true">
          <Trans
            ns="squad"
            i18nKey="element.removal"
            values={{ element: elementsById[lastChange.element].web_name }}
            components={{ strong: <strong /> }}
          >
            <strong>{"{{element}}"}</strong> has been removed from your squad
          </Trans>
        </span>
      </Alert>
    );
  } else if (autoPickStatus === "success") {
    latestAction = (
      <Alert variant="success" isInline isCentered>
        <span role="status" aria-atomic="true">
          {t("manageSquad.autoPickSuccess", "Auto Pick successful")}
        </span>
      </Alert>
    );
  } else if (autoPickStatus === "failed") {
    const knownMessage = autoPickErrorCode
      ? autopickErrorMessages[autoPickErrorCode]
      : undefined;
    const errorMessage = knownMessage ?? defaultAutopickErrorMessage;
    latestAction = (
      <Alert variant="error" isInline isCentered>
        <span role="status" aria-atomic="true">
          {errorMessage}
        </span>
      </Alert>
    );
  }

  const hasErrors = Boolean(Object.keys(errors).length);

  const showStatus = latestAction || errors.overTeamLimit;

  const errorCode = getCodeFromError(error);

  if (error) {
    const handledErrorCodes = ["price_changed"];
    if (handledErrorCodes.indexOf(errorCode) === -1) {
      window.location.reload();
      return null;
    }
  }

  // Check if the user has just used the team builder to generate a complete team during squad selection
  const autoPickedTeam =
    mode === "selection" &&
    autopickChoice === "show_tutorial" &&
    !canUserAutocomplete &&
    !hasErrors;

  const disableEnterSquad =
    isAwaitingEntry || Boolean(entry) || hasErrors || !entryDetails;

  return (
    <OverlayProvider defaultOverlay="opponent" page="squad">
      <div className={contentMain}>
        <SectionHeader
          title={title}
          description={
            t(
              "manageSquad.description",
              "Select a maximum of {{squadTeamLimit}} players from a single team or 'Auto Pick' if you are short of time.",
              {
                squadTeamLimit: rules?.squad_team_limit || 3,
              },
            ) as string
          }
        />
        <DeadlineBar />
        {scoreboard}
        <div role="status">
          {showStatus && (
            <>
              {latestAction && latestAction}
              {errors.overTeamLimit && (
                <Alert variant="error" isInline isCentered>
                  {t(
                    "manageSquad.tooManyPlayers",
                    "Too many players selected from",
                  )}{" "}
                  <strong>
                    {errors.overTeamLimit
                      .map((team) => teamsById[team].name)
                      .join(", ")}
                  </strong>
                </Alert>
              )}
            </>
          )}
        </div>

        <PitchControls
          activeView={activeView}
          handleViewChange={handleViewChange}
        />
        {activeView === "pitch" && (
          <div className={edgeToEdge}>
            <SquadPitch
              renderElementMenu={handleShowMenuForElement}
              handleOpenElemListSheet={handleOpenElemListSheet}
              setPitchUnitRef={setPitchUnitRef}
            />
          </div>
        )}
        {activeView === "list" && (
          <>
            {mode === "selection" && (
              <SquadListTable
                handleOpenElemListSheet={handleOpenElemListSheet}
                proposedElements={proposedElements}
                handleElementClick={handleShowMenuForElement}
              />
            )}
            {mode === "transfers" && (
              <TransfersListTable
                handleOpenElemListSheet={handleOpenElemListSheet}
                proposedElements={proposedElements}
                handleElementClick={handleShowMenuForElement}
              />
            )}
          </>
        )}
        <SquadActionsBar
          handleAutoPick={autoPick}
          handleAutoPickAgain={autoPickAgain}
          handleReset={handleReset}
          isAutoPickDisabled={autoPickStatus === "picking"}
          isAutoPickTeam={autoPickedTeam}
          isValidSquad={!canUserAutocomplete}
          isResetDisabled={!canUserReset}
        >
          {mode === "selection" ? (
            <Button
              type="submit"
              isDisabled={disableEnterSquad}
              onPress={() => {
                submitEntry();
                setIsAwaitingEntry(true);
              }}
            >
              {isAwaitingEntry
                ? t("manageSquad.enteringSquad", "Entering Squad…")
                : t("manageSquad.enterSquad", "Enter Squad")}
            </Button>
          ) : mode === "transfers" ? (
            <ConfirmTransfersSheet isTriggerDisabled={hasErrors} />
          ) : null}
        </SquadActionsBar>
        {mode === "selection" && isTutorialEnabled && (
          <Button
            appearance="outlined"
            onPress={handleAutoPickWithTutorial}
            fullWidth
          >
            {t("manageSquad.autoPickWithTutorial", "Auto Pick With Tutorial")}
          </Button>
        )}
      </div>
      <PlayerPreviewSheet
        element={element}
        open={Boolean(element)}
        handleClose={handleClosePlayerSheet}
        actionButtons={({ handleViewFullProfile }) => {
          if (!element) return null;

          return (
            <PlayerActions
              actionMode={actionMode}
              handleViewFullProfile={handleViewFullProfile}
              element={element}
              position={elementPosition}
              handleClosePlayerSheet={handleClosePlayerSheet}
              handleOpenElemListSheet={handleOpenElemListSheet}
              focusPitchUnit={focusPitchUnit}
            />
          );
        }}
      />
      {mode === "selection" && <Onboarding isOpen={isOnboardingOpen} />}
      <TutorialWarningDialog
        isOpen={tutorialWarningDialogOpen}
        onConfirm={() => {
          setTutorialWarningDialogOpen(false);
          handleReset();
          reopenForTutorial();
        }}
        onCancel={() => setTutorialWarningDialogOpen(false)}
      />
    </OverlayProvider>
  );
};

export default ManageSquad;
